import { type Capability, hasCapability } from './permissions.js';
import { type Role } from './roles.js';
import {
  TRANSACTION_STATUSES,
  TRANSACTION_STATUS_SUCCESORS,
  type TransactionStatus,
} from './numbering.js';

/**
 * Phase 2B - controlled status workflow.
 *
 *   PENDING -> WASHING -> FOLDING -> READY -> RELEASED -> DONE
 *
 * Each edge carries the capability the actor's role must hold. ADMIN holds
 * every capability, so admins can drive the whole workflow; employees may
 * only advance the stages their role owns:
 *   - WASHING_EMPLOYEE: PENDING -> WASHING, WASHING -> FOLDING
 *   - FOLDING_EMPLOYEE: FOLDING -> READY
 *   - release/close (money leaving the counter) stays with transactions.manage
 *
 * The matrix is descriptive only - authorization is re-checked server-side
 * on every request (the frontend is never trusted).
 */
export interface StatusTransitionRule {
  to: TransactionStatus;
  capability: Capability;
  /** Action label shown on the button that performs this transition. */
  label: string;
}

export const STATUS_TRANSITIONS: Record<TransactionStatus, StatusTransitionRule[]> = {
  PENDING: [{ to: 'WASHING', capability: 'washing.manage', label: 'Start washing' }],
  WASHING: [{ to: 'FOLDING', capability: 'washing.manage', label: 'Hand off to folding' }],
  FOLDING: [{ to: 'READY', capability: 'folding.manage', label: 'Mark ready for pickup' }],
  READY: [{ to: 'RELEASED', capability: 'transactions.manage', label: 'Release to customer' }],
  RELEASED: [{ to: 'DONE', capability: 'transactions.manage', label: 'Close transaction' }],
  DONE: [],
};

/** Rule for a single edge, or undefined when the edge does not exist. */
export function transitionRule(
  from: TransactionStatus,
  to: TransactionStatus,
): StatusTransitionRule | undefined {
  return STATUS_TRANSITIONS[from].find((rule) => rule.to === to);
}

/** True when the from -> to edge exists in the workflow matrix. */
export function isValidTransition(from: TransactionStatus, to: TransactionStatus): boolean {
  return transitionRule(from, to) !== undefined;
}

/**
 * The edge that leads INTO a status (the linear workflow has at most one).
 * Undefined for PENDING, the entry point. Lets the server answer "your role
 * may never move a transaction to <status>" (403) before "that step does
 * not exist" (409).
 */
export function entryRule(to: TransactionStatus): StatusTransitionRule | undefined {
  for (const rules of Object.values(STATUS_TRANSITIONS)) {
    const found = rules.find((rule) => rule.to === to);
    if (found) return found;
  }
  return undefined;
}

/**
 * True when the role holds the capability required to enter `to`.
 * Statuses with no entry edge (PENDING) impose no capability gate - the
 * edge-existence check decides those cases.
 */
export function canEnterStatus(role: Role, to: TransactionStatus): boolean {
  const entry = entryRule(to);
  return entry === undefined || hasCapability(role, entry.capability);
}

/** Direct successors of a status (empty for DONE = terminal). */
export function nextStatuses(status: TransactionStatus): TransactionStatus[] {
  return STATUS_TRANSITIONS[status].map((rule) => rule.to);
}

/** Transitions the given role is actually allowed to perform right now. */
export function availableTransitions(
  role: Role,
  status: TransactionStatus,
): StatusTransitionRule[] {
  return STATUS_TRANSITIONS[status].filter((rule) => hasCapability(role, rule.capability));
}

/** Server-side (and UI-mirroring) authorization check for one transition. */
export function canTransition(
  role: Role,
  from: TransactionStatus,
  to: TransactionStatus,
): boolean {
  const rule = transitionRule(from, to);
  return rule !== undefined && hasCapability(role, rule.capability);
}

export function isTerminalStatus(status: TransactionStatus): boolean {
  return STATUS_TRANSITIONS[status].length === 0;
}

/**
 * Self-check used by tests: the hand-written capability matrix must agree
 * with the successor matrix in numbering.ts, so the two can never drift.
 */
export function workflowMatrixIsConsistent(): boolean {
  for (const status of TRANSACTION_STATUSES) {
    const fromMatrix = STATUS_TRANSITIONS[status].map((r) => r.to).sort();
    const fromSuccessors = [...TRANSACTION_STATUS_SUCCESORS[status]].sort();
    if (fromMatrix.length !== fromSuccessors.length) return false;
    if (fromMatrix.some((value, index) => value !== fromSuccessors[index])) return false;
  }
  return true;
}

